import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from '@mui/material';
import { NavLink } from 'react-router-dom';
import { Spacing } from '../../design-system/spacing';
import { Kind } from '../../types/kind';
import { styled } from '../../utils/styled';
import { Box } from '../Box';
import { Stack } from '../Stack';

export const ItemWrapper = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'color',
})<{ color: string }>`
  position: relative;
  box-sizing: border-box;
  align-items: center;
  color: ${({ color }) => color};
  flex-direction: row;
  justify-content: flex-start;
  width: 100%;
  height: 50px;
  padding: ${({ theme }) => theme.spacing(Spacing.Small)};
`;

export const StyledBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'color' && prop !== 'expanded',
})<{ color: string; expanded: boolean }>`
  color: ${({ color }) => color};
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
  & svg {
    display: block;
  }
  ${({ theme, expanded }) =>
    expanded
      ? `margin-right: ${theme.spacing(Spacing.Small)};`
      : `margin-right: 0;`}
`;

export const StyledNavItemLabel = styled(Typography)`
  margin: 0;
  line-height: 21px;
`;

export const LabelContainer = styled('span', {
  shouldForwardProp: (prop) => prop !== 'expanded',
})<{ expanded: boolean }>`
  overflow: hidden;
  white-space: nowrap;
  transition: ${({ theme }) =>
    theme.transitions.create(['max-width', 'opacity'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    })};
  ${({ expanded }) =>
    expanded
      ? `
    position: relative;
    display: block;
    flex: 1 1 auto;
    min-width: 0;
    max-width: 320px;
    opacity: 1;
  `
      : `
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    flex: none;
    max-width: none;
    opacity: 0;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    pointer-events: none;
    border: 0;
  `}
`;
export const StyledAccordionDetails = styled(AccordionDetails)`
  margin-bottom: ${({ theme }) => theme.spacing(Spacing.Tiny)};
`;

export const NavItemWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'kind',
})<{ kind: Kind }>`
  white-space: nowrap;
  background-color: ${({ theme, kind }) =>
    kind === 'dark'
      ? theme.palette.background.dark
      : theme.palette.background.paper};
  margin: 0;
  margin-bottom: ${({ theme }) => theme.spacing(Spacing.Small)};
  border-radius: ${({ theme }) => theme.borderRadii.outer};
  color: ${({ theme, kind }) =>
    kind === 'dark'
      ? theme.palette.text.secondaryInverted
      : theme.palette.text.secondary};

  &:hover {
    background-color: ${({ theme, kind }) =>
      kind === 'dark' ? theme.palette.neutral[800] : theme.palette.paper[400]};
  }
  width: 100%;
`;

export const StyledAccordionSummary = styled(AccordionSummary, {
  shouldForwardProp: (prop) => prop !== 'kind',
})<{ kind: Kind }>`
  background-color: ${({ theme, kind }) =>
    kind === 'dark'
      ? theme.palette.background.dark
      : theme.palette.background.paper};
  margin-bottom: ${({ theme }) => theme.spacing(Spacing.Tiny)};
  padding: ${({ theme }) => theme.spacing(Spacing.Small)};
  border-radius: ${({ theme }) => theme.borderRadii.outer};
  color: ${({ theme, kind }) =>
    kind === 'dark'
      ? theme.palette.text.secondaryInverted
      : theme.palette.text.secondary};

  &:hover {
    background-color: ${({ theme, kind }) =>
      kind === 'dark' ? theme.palette.neutral[800] : theme.palette.paper[400]};
  }
`;

export const StyledTopLevelNavLink = styled(NavLink)`
  display: block;
  width: 100%;
  text-decoration: none;
  color: inherit;
`;

export const AccordionLink = styled(NavLink)`
  text-decoration: none;
  color: inherit;

  &:first-of-type > div {
    border-top-left-radius: ${({ theme }) => theme.borderRadii.outer};
    border-top-right-radius: ${({ theme }) => theme.borderRadii.outer};
  }
  &:last-child > div {
    border-bottom-left-radius: ${({ theme }) => theme.borderRadii.outer};
    border-bottom-right-radius: ${({ theme }) => theme.borderRadii.outer};
  }
`;

export const AccordionInnerItems = styled('div', {
  shouldForwardProp: (prop) => prop !== 'kind',
})<{ kind: Kind }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background-color: ${({ theme, kind }) =>
    kind === 'dark' ? theme.palette.neutral[900] : theme.palette.paper[200]};
  color: ${({ theme, kind }) =>
    kind === 'dark'
      ? theme.palette.text.secondaryInverted
      : theme.palette.text.secondary};
  padding: ${({ theme }) => theme.spacing(Spacing.Small)};
  ${({ theme }) => theme.spacing(Spacing.Medium)};

  &:hover {
    background-color: ${({ theme, kind }) =>
      kind === 'dark' ? theme.palette.neutral[800] : theme.palette.paper[400]};
  }
`;

export const StyledAccordion = styled(Accordion, {
  shouldForwardProp: (prop) => prop !== 'kind',
})<{ kind: Kind }>`
  box-shadow: none;
  width: 100%;
  background-color: transparent;

  color: ${({ theme, kind }) =>
    kind === 'dark'
      ? theme.palette.text.secondaryInverted
      : theme.palette.text.secondary};

  &::before {
    opacity: 0;
  }

  &:hover {
    border-radius: ${({ theme }) => theme.borderRadii.outer};
  }

  &.Mui-expanded {
    margin: 0;
  }

  & .MuiAccordionSummary-expandIconWrapper {
    color: ${({ theme, kind }) =>
      kind === 'dark'
        ? theme.palette.text.secondaryInverted
        : theme.palette.text.secondary};
  }
  & .MuiAccordionSummary-root,
  .MuiAccordionSummary-root.Mui-expanded {
    min-height: 40px;
  }
  & .MuiAccordionSummary-content,
  .MuiAccordionSummary-content.Mui-expanded {
    margin: 0;
  }
  & .MuiAccordionDetails-root {
    padding: 0;
  }
`;
